import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Shield, Filter, Move, AlertCircle, CheckCircle2, Zap, ArrowRight, Settings2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../services/api';

const RulesModal = ({ user, onClose }) => {
    const [rules, setRules] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [newRule, setNewRule] = useState({
        name: '',
        conditionType: 'FROM',
        conditionValue: '',
        actionType: 'MOVE_TO',
        actionValue: 'Archive',
        stopProcessing: true,
        active: true
    });

    const fetchRules = async () => {
        setIsLoading(true);
        try {
            const response = await apiFetch('/mails/rules/all');
            if (response.ok) {
                const data = await response.json();
                setRules(data);
            }
        } catch (error) {
            console.error('Failed to fetch rules:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRules();
    }, [user.email]);

    const handleCreateRule = async () => {
        if (!newRule.name || !newRule.conditionValue) {
            alert('Please fill in Name and Condition value');
            return;
        }

        try {
            const response = await apiFetch('/mails/rules', {
                method: 'POST',
                body: JSON.stringify(newRule)
            });

            if (response.ok) {
                setIsAdding(false);
                setNewRule({
                    name: '',
                    conditionType: 'FROM',
                    conditionValue: '',
                    actionType: 'MOVE_TO',
                    actionValue: 'Archive',
                    stopProcessing: true,
                    active: true
                });
                fetchRules();
            }
        } catch (error) {
            console.error('Failed to create rule:', error);
        }
    };

    const handleDeleteRule = async (id) => {
        try {
            const response = await apiFetch(`/mails/rules/${id}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                fetchRules();
            }
        } catch (error) {
            console.error('Failed to delete rule:', error);
        }
    };

    const handleRunRules = async () => {
        setIsRunning(true);
        try {
            const response = await apiFetch('/mails/rules/run?folder=INBOX', {
                method: 'POST'
            });
            if (response.ok) {
                alert('Rules applied to INBOX successfully');
            }
        } catch (error) {
            console.error('Failed to run rules:', error);
        } finally {
            setIsRunning(false);
        }
    };

    const customFolders = JSON.parse(localStorage.getItem(`xemail_custom_folders_${user.email}`) || '[]');
    const allFolders = ['Archive', 'Trash', 'Sent', ...customFolders];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                background: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)'
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 30 }}
                className="premium-card animate-premium-in"
                style={{
                    width: '100%',
                    maxWidth: '640px',
                    maxHeight: '85vh',
                    background: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Top Gradient Bar */}
                <div style={{ height: '4px', background: 'linear-gradient(90deg, #3b82f6, #6366f1, #3b82f6)', backgroundSize: '200% auto' }} />

                {/* Header */}
                <header style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid var(--border-glass)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'rgba(255,255,255,0.8)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div className="premium-icon-container" style={{ background: 'var(--primary-glow)', color: 'var(--primary)' }}>
                            <Settings2 size={24} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '900', letterSpacing: '-0.5px', textTransform: 'uppercase', color: '#0f172a' }}>Email Rules</h2>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' }}>Automatically organize your mail</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {!isAdding && rules.length > 0 && (
                            <button
                                onClick={handleRunRules}
                                disabled={isRunning}
                                style={{
                                    fontSize: '0.7rem',
                                    fontWeight: '900',
                                    textTransform: 'uppercase',
                                    padding: '8px 16px',
                                    borderRadius: '10px',
                                    background: isRunning ? 'var(--bg-secondary)' : 'var(--primary-glow)',
                                    color: isRunning ? 'var(--text-muted)' : 'var(--primary)',
                                    border: '1px solid transparent',
                                    transition: 'all 0.2s'
                                }}
                                className={isRunning ? "" : "btn-hover-light"}
                            >
                                {isRunning ? 'Running...' : 'Apply to Inbox Now'}
                            </button>
                        )}
                        <button onClick={onClose} className="btn-ghost" style={{ padding: '10px', borderRadius: '14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-glass)' }}>
                            <X size={20} className="text-slate-500" />
                        </button>
                    </div>
                </header>

                <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', padding: '32px' }}>
                    <AnimatePresence mode="wait">
                        {isAdding ? (
                            <motion.div
                                key="add-rule"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
                            >
                                <div className="info-card">
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', color: 'var(--text-muted)' }}>Rule Name</label>
                                    <input
                                        value={newRule.name}
                                        onChange={e => setNewRule({ ...newRule, name: e.target.value })}
                                        placeholder="e.g. Move Work Emails"
                                        style={{
                                            width: '100%',
                                            padding: '14px',
                                            borderRadius: '12px',
                                            border: '1px solid var(--border-glass)',
                                            background: '#FFFFFF',
                                            color: '#0f172a',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            outline: 'none'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="info-card">
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', color: 'var(--text-muted)' }}>When an email arrives...</label>
                                        <select
                                            value={newRule.conditionType}
                                            onChange={e => setNewRule({ ...newRule, conditionType: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border-glass)',
                                                background: '#FFFFFF',
                                                color: '#0f172a',
                                                fontWeight: '700',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <option value="FROM">Sender is (From)</option>
                                            <option value="TO">Recipient is (To)</option>
                                            <option value="SUBJECT">Subject contains</option>
                                            <option value="KEYWORDS">Email has keywords</option>
                                        </select>
                                    </div>
                                    <div className="info-card">
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', color: 'var(--text-muted)' }}>Search For...</label>
                                        <input
                                            value={newRule.conditionValue}
                                            onChange={e => setNewRule({ ...newRule, conditionValue: e.target.value })}
                                            placeholder="Keywords or email address"
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border-glass)',
                                                background: '#FFFFFF',
                                                color: '#0f172a',
                                                fontWeight: '600',
                                                fontSize: '0.9rem',
                                                outline: 'none'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="info-card">
                                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', color: 'var(--text-muted)' }}>Then do this...</label>
                                        <select
                                            value={newRule.actionType}
                                            onChange={e => setNewRule({ ...newRule, actionType: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                borderRadius: '12px',
                                                border: '1px solid var(--border-glass)',
                                                background: '#FFFFFF',
                                                color: '#0f172a',
                                                fontWeight: '700',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <option value="MOVE_TO">Move to folder</option>
                                            <option value="DELETE">Delete permanently</option>
                                        </select>
                                    </div>
                                    {newRule.actionType === 'MOVE_TO' && (
                                        <div className="info-card">
                                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', color: 'var(--text-muted)' }}>Select Folder</label>
                                            <select
                                                value={newRule.actionValue}
                                                onChange={e => setNewRule({ ...newRule, actionValue: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '14px',
                                                    borderRadius: '12px',
                                                    border: '1px solid var(--border-glass)',
                                                    background: '#FFFFFF',
                                                    color: '#0f172a',
                                                    fontWeight: '700',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                {allFolders.map(f => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>

                                <div className="info-card" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ position: 'relative', width: '20px', height: '20px' }}>
                                        <input
                                            type="checkbox"
                                            id="stopProcessing"
                                            checked={newRule.stopProcessing}
                                            onChange={e => setNewRule({ ...newRule, stopProcessing: e.target.checked })}
                                            style={{ margin: 0, width: '20px', height: '20px', cursor: 'pointer' }}
                                        />
                                    </div>
                                    <label htmlFor="stopProcessing" style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--text-muted)', cursor: 'pointer' }}>Stop processing more rules if this matches</label>
                                </div>

                                <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                                    <button onClick={handleCreateRule} className="btn-primary" style={{ flex: 1, padding: '16px', borderRadius: '16px' }}>
                                        <Zap size={18} />
                                        <span>Save Rule</span>
                                    </button>
                                    <button onClick={() => setIsAdding(false)} className="btn-ghost" style={{ flex: 1, border: '1px solid var(--border-glass)', borderRadius: '16px', fontWeight: '900', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                        Cancel
                                    </button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="list-rules"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                            >
                                <button
                                    onClick={() => setIsAdding(true)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        padding: '24px',
                                        borderRadius: '24px',
                                        border: '2px dashed var(--border-glass)',
                                        color: 'var(--primary)',
                                        background: 'transparent',
                                        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                                        cursor: 'pointer'
                                    }}
                                    className="btn-hover-light"
                                >
                                    <div style={{ padding: '8px', background: 'var(--primary-glow)', borderRadius: '12px' }}>
                                        <Plus size={20} />
                                    </div>
                                    <span style={{ fontSize: '0.9rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Create a New Rule</span>
                                </button>

                                {isLoading ? (
                                    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.1em' }} className="animate-pulse">Loading Rules...</div>
                                ) : rules.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
                                        <div style={{ width: '80px', height: '80px', background: 'var(--bg-secondary)', borderRadius: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', opacity: 0.5 }}>
                                            <Filter size={40} />
                                        </div>
                                        <p style={{ fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>No Active Rules</p>
                                        <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>Emails will stay in your inbox unless you create a rule.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {rules.map(rule => (
                                            <div
                                                key={rule.id}
                                                className="info-card"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '20px'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', overflow: 'hidden' }}>
                                                    <div className="premium-icon-container" style={{ background: 'var(--primary-glow)', color: 'var(--primary)', flexShrink: 0 }}>
                                                        <Zap size={18} />
                                                    </div>
                                                    <div style={{ overflow: 'hidden' }}>
                                                        <h3 style={{ fontSize: '0.9rem', fontWeight: '900', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.name}</h3>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>Matcher: {rule.conditionType}</span>
                                                            <ArrowRight size={10} style={{ opacity: 0.5 }} />
                                                            <span className="enterprise-badge" style={{ background: '#0f172a', color: '#fff', border: 'none' }}>{rule.actionType}</span>
                                                            <span style={{ fontSize: '0.75rem', fontWeight: '800', color: 'var(--primary)' }}>{rule.actionValue}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteRule(rule.id)}
                                                    style={{ padding: '10px', color: '#ef4444', background: '#fee2e2', borderRadius: '12px', border: '1px solid transparent', flexShrink: 0 }}
                                                    className="btn-hover-light"
                                                    title="Delete Rule"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Footer Tip */}
                <footer style={{
                    padding: '20px 32px',
                    background: 'var(--bg-secondary)',
                    borderTop: '1px solid var(--border-glass)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: 'var(--text-muted)'
                }}>
                    <div style={{ color: 'var(--primary)' }}><Shield size={16} /></div>
                    <span style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Safety Note: Rules run in order to keep your inbox organized automatically.</span>
                </footer>
            </motion.div>
        </motion.div>
    );
};

export default RulesModal;
